import { z } from 'zod';

import { AccountingClassificationSchema } from './ap/accounting-classification.js';

const CategoriaDecisionSchema = z.union([
  z.object({
    shouldSet: z.literal(true),
    segmentId: z.string().uuid(),
    segmentValueId: z.string().uuid(),
    description: z.string().max(120).nullable(),
  }),
  z.object({
    // Allow tenants without the Categoria segment to return categoria with nulls
    // (or omit categoria entirely). This prevents the entire decision from
    // failing schema parsing and losing classification suggestions.
    shouldSet: z.literal(false),
    segmentId: z.string().uuid().nullable(),
    segmentValueId: z.string().uuid().nullable(),
    description: z.string().max(120).nullable(),
  }),
]);

/**
 * AI decision for matching an NF-e to a bill.
 *
 * Used by the worker to decide whether to link a Focus NFe received NF to
 * an existing bill, create a new bill, or ignore the NF entirely.
 */
export const NfBillMatchingDecisionSchema = z.object({
  /** Short reasoning for auditability (kept compact). */
  reasoning: z.string().max(4000),

  categoria: CategoriaDecisionSchema.optional(),

  classification: z
    .object({
      shouldSet: z.boolean(),
      costCenterId: z.string().uuid().nullable(),
      departmentId: z.string().uuid().nullable(),
      managerialAccountId: z.string().uuid().nullable(),
      glAccountId: z.string().uuid().nullable(),
      notes: z.string().max(500).nullable(),
    })
    .optional(),

  /**
   * Historical reuse: deterministically copy classifications from a prior
   * approved vendor bill. When shouldReuse=true and referenceBillId is set,
   * the worker will copy classifications from that bill.
   */
  historicalReuse: z
    .object({
      /** When true, worker should copy classifications from referenceBillId. */
      shouldReuse: z.boolean(),
      /** UUID of the approved+ bill to copy classifications from. */
      referenceBillId: z.string().uuid().nullable(),
      /** Optional notes for audit trail. */
      notes: z.string().max(500).nullable().optional(),
    })
    .optional(),

  action: z.enum(['LINK_EXISTING', 'CREATE_NEW', 'IGNORE']),
  /** Target bill to link when action is LINK_EXISTING. */
  targetBillId: z.string().uuid().nullable(),

  /** Confidence 0–100 from the LLM. */
  confidence: z.number().min(0).max(100),

  /** Accounting classification decision from the LLM (optional). */
  accountingClassification: AccountingClassificationSchema.optional(),
});

export type NfBillMatchingDecision = z.infer<typeof NfBillMatchingDecisionSchema>;
